/*eslint max-lines-per-function: ["error", 200]*/
import React, { useMemo, useState } from 'react';
import { Card, Tab } from 'react-bootstrap';

import Tabs from '../../../app/components/Tabs';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { useAuth } from '../../../common/hooks/useAuth';
import { Role } from '../../../common/spdCore/authorization/roles';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { GetPaymentDto } from '../../../common/spdCore/autogenerated/spdApiClient';
import { Payments } from '../../components/Payments/Payments';
import { PaymentEditor } from '../../components/Payments/PaymentEditor';

export const PaymentsView: React.FC = () => {
  const spd = useSpdCore();
  const auth = useAuth();

  const [refresh, setRefresh] = useState<boolean>(false);
  const [selectedPayment, setSelectedPayment] = useState<GetPaymentDto>();
  const [showEditor, setShowEditor] = useState(false);

  const handleRowClick = (paymentId: string) => {
    spd.payments
      .getById(paymentId)
      .then(payment => {
        setSelectedPayment(payment);
        setShowEditor(true);
      })
      .catch(error => {
        console.error('Error fetching payment:', error);
      });
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
  };

  const handleSavePayment = async (payment: GetPaymentDto) => {
    try {
      await spd.payments.update(payment.id, payment);
      setRefresh(!refresh);
    } catch (error) {
      console.error('Error saving payment:', error);
    }
  };

  const getData = useMemo(
    () => () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        spd.payments.getAllByPortion(
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
          () => setRefresh(!refresh),
        )) as GridGetterFunction,
    [spd, refresh],
  );

  const getSellDocumentNotIssuedData = useMemo(
    () => () =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        spd.payments.getSellDocumentNotIssuedByPortion(
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          filterColumns,
          () => setRefresh(!refresh),
        )) as GridGetterFunction,
    [spd, refresh],
  );

  const getCount = useMemo(
    () => (status?: string) =>
      (async (filterText, filterColumns) => {
        const totalCount = await spd.payments.getAllCount(
          filterText,
          filterColumns,
        );
        if (!status) {
          return totalCount;
        }
      }) as GridCountFunction,
    [spd],
  );

  const getSellDocumentNotIssuedCount = useMemo(
    () => (status?: string) =>
      (async (filterText, filterColumns) => {
        const totalCount = await spd.payments.getSellDocumentNotIssuedCount(
          filterText,
          filterColumns,
        );
        if (!status) {
          return totalCount;
        }
      }) as GridCountFunction,
    [spd],
  );

  return (
    <>
      <Card>
        <Tabs
          defaultActiveKey="sellDocumentNotIssued"
          id="payments"
          className="mb-3"
        >
          <Tab
            eventKey="sellDocumentNotIssued"
            title="Bez dokumentu sprzedaży"
            className="m-3"
          >
            <Payments
              getData={getSellDocumentNotIssuedData()}
              getCount={getSellDocumentNotIssuedCount()}
              refresh={refresh}
              onPaymentRowClick={handleRowClick}
              showCreateInvoiceAction={auth.check([Role.Admin, Role.Finance])}
            />
          </Tab>
          <Tab eventKey="search" title="Wszystkie" className="m-3">
            <Payments
              getData={getData()}
              getCount={getCount()}
              refresh={refresh}
              onPaymentRowClick={handleRowClick}
            />
          </Tab>
        </Tabs>
      </Card>
      <PaymentEditor
        payment={selectedPayment}
        show={showEditor}
        onHide={handleCloseEditor}
        onSave={handleSavePayment}
      />
    </>
  );
};
